import DeviceKeysPicker from '../DeviceKeysPicker';
import React, { useCallback, useEffect, useState } from 'react';
import {
    BoundItem,
    GroupSchemaClient,
    SchemaGroup,
    SchemaGroupField,
    parseGroupFields,
} from '../../api/schema';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import {
    SchemaField,
    parseFieldEnums,
    suggestFieldLabel,
} from '../../api/schemaField';
import {
    SchemaFieldKeySelect,
    useSchemaFieldDict,
} from '../SchemaFieldKeySelect';
import Modal from '../Modal';
import { useCategoryTree } from '../MaterialFieldInputs';
import { DictCategory, listDictCategories } from '../../api/dict';
import { ProcessArch, listProcessArchs } from '../../api/parameter';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import SearchSelect from '../SearchSelect';
import {
    Layout,
    fitsLayout,
    parseLayout,
    stringifyLayout,
} from '../../utils/fieldGroups';
import { useLayoutEditor } from '../fieldLayout/useLayoutEditor';
import {
    LayoutFieldList,
    LayoutTabActions,
} from '../fieldLayout/LayoutEditorParts';
import { ENABLED_OPTS } from '../../utils/searchOptions';

const LAYOUT_OPTS = [
    { value: 'flat', label: '常规 (字段平铺)' },
    { value: 'matrix', label: '矩阵 (行来自字典 × 列为字段)' },
];

// 字段组编辑器 (2026-09-20 从 FieldGroupSchemaManager 抽出).
//
// 抽出的原因: 「新建字段组」不再只发生在模板管理页 —— 建设备/建材料的向导里
// 发现缺一个组时, 得能就地补一个, 不然只能中断向导跑去模板页再回来重填。
// 两处用的是同一份表单 (组编码/显示名/布局/字段清单), 所以表单与保存逻辑放这里, 两边共用。
//
// 向导里用 lockScope: **只锁归属模板** (设备=工艺框架 / 材料=一级类别, 由当前上下文决定),
// 其余选项 (绑定二级类别 / 绑定设备 / 布局 / 字段) 与模板管理页的新建完全一样 —— 建组这件事
// 在哪里发起都是同一件事, 少给选项只会逼人建完再去模板页补。

export interface FieldGroupEditorState {
    isNew: boolean;
    groupKey: string;
    label: string;
    src: string;
    cat1: string;
    /** 组内二级类别绑定 (仅材料理化组): 空 = 该类别全部材料 */
    cat2Keys: string[];
    archCode: string;
    /** 组内设备绑定 (仅设备组): 空 = 该框架全部设备 */
    deviceKeys: string[];
    /** 打开编辑时的原模板编码 (设备=archCode / 材料=cat1): 组按 (groupKey, 模板) 实例化, update 需用原值定位 */
    origScopeCode: string;
    layout: string;
    rowSource: string;
    rowLabel: string;
    /** 组内 页签 → 分组 结构 (仅设备组, 2026-10-23); null = 平铺 */
    fieldLayout: Layout;
    fields: SchemaGroupField[];
    isEnabled: boolean;
}

/** 新建态: 从模板区块/向导进入时预填归属 (设备=archCode / 材料=cat1), 组必须归属某模板. */
export function newFieldGroupEditor(scope?: {
    archCode?: string;
    cat1?: string;
}): FieldGroupEditorState {
    return {
        isNew: true,
        groupKey: '',
        label: '',
        src: '',
        cat1: scope?.cat1 || '',
        cat2Keys: [],
        archCode: scope?.archCode || '',
        deviceKeys: [],
        origScopeCode: '',
        layout: 'flat',
        rowSource: '',
        rowLabel: '',
        fieldLayout: null,
        fields: [{ key: '', label: '' }],
        isEnabled: true,
    };
}

/** 编辑态: 从已有组回填 (archScope 决定模板编码取 archCode 还是 cat1). */
export function fieldGroupEditorFrom(
    g: SchemaGroup,
    archScope: boolean,
): FieldGroupEditorState {
    return {
        isNew: false,
        groupKey: g.groupKey,
        label: g.label,
        src: g.src,
        cat1: g.cat1 || '',
        // 旧单值 cat2 兜底并入清单 (存量未迁移行编辑时不静默丢绑定)
        cat2Keys: g.cat2Keys?.length ? g.cat2Keys : g.cat2 ? [g.cat2] : [],
        archCode: g.archCode || '',
        deviceKeys: g.deviceKeys || [],
        origScopeCode: archScope ? g.archCode || '' : g.cat1 || '',
        layout: g.layout || 'flat',
        rowSource: g.rowSource || '',
        rowLabel: g.rowLabel || '',
        fieldLayout: parseLayout(g.fieldLayout),
        fields: parseGroupFields(g.fields),
        isEnabled: g.isEnabled,
    };
}

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

export interface FieldGroupEditorModalProps {
    editor: FieldGroupEditorState;
    setEditor: (e: FieldGroupEditorState) => void;
    onClose: () => void;
    /** 保存成功 (admin 即时生效 / 普通用户已提审) 后回调: 调用方刷新自己的组清单 */
    onSaved: (groupKey: string) => void;
    client: GroupSchemaClient;
    /** 该类 Schema 的名称, 如 "材料理化" / "设备" */
    domainLabel: string;
    /** 按材料类别限定作用域 (仅材料理化组开启) */
    categoryScope?: boolean;
    /** 支持矩阵布局 (材料理化 + 设备组均开启) */
    matrixLayout?: boolean;
    /** 按工艺框架作用域 (仅设备组开启) */
    archScope?: boolean;
    /** 字段可标必填 (仅 SKU 规格模板开启, 2026-10-14): 每行多一个「必填」勾选, 保存时带 required */
    fieldRequired?: boolean;
    /** 归属模板锁定 (向导内新建): 不显示归属/绑定选择, 用传进来的 archCode / cat1 */
    lockScope?: boolean;
    /** lockScope 时显示的归属说明, 如 "激光框机 (laser-frame)" */
    scopeHint?: string;
    /**
     * 给出时不调接口, 把新建请求体交给调用方 (设备向导: 放进套件 newDicts.deviceSchemas,
     * 随整包一个工单落库, 不再单独提审). 此时 onSaved 不会被调用.
     */
    onDraft?: (body: FieldGroupDraftBody) => void;
}

/** FieldGroupDraftBody 新建字段组的请求体 (与 client.create 同形, 不含归属模板 archCode/cat1). */
export interface FieldGroupDraftBody {
    groupKey: string;
    label: string;
    src: string;
    fields: string;
    isEnabled: boolean;
    deviceKeys?: string[];
    cat2Keys?: string[];
    layout?: string;
    rowSource?: string;
    rowLabel?: string;
    /** 仅设备组 (2026-10-23) */
    fieldLayout?: string | null;
}

function FieldGroupEditorModal({
    editor,
    setEditor,
    onClose,
    onSaved,
    client,
    domainLabel,
    categoryScope,
    matrixLayout,
    archScope,
    fieldRequired,
    lockScope,
    scopeHint,
    onDraft,
}: FieldGroupEditorModalProps) {
    // 字典类目 (矩阵行来源枚举下拉); 特殊行来源 mm_group = 加工模式组.
    const [enumCats, setEnumCats] = useState<DictCategory[]>([]);
    useEffect(() => {
        if (!matrixLayout) return;
        listDictCategories()
            .then(setEnumCats)
            .catch(() => setEnumCats([]));
    }, [matrixLayout]);
    const enumLabel = useCallback(
        (code: string) =>
            code === 'mm_group'
                ? '加工模式'
                : enumCats.find((c) => c.code === code)?.label || code,
        [enumCats],
    );

    // 归属模板候选 (设备组); lockScope 时归属已定, 不用拉
    const [archs, setArchs] = useState<ProcessArch[]>([]);
    useEffect(() => {
        if (!archScope || lockScope) return;
        listProcessArchs()
            .then(setArchs)
            .catch(() => setArchs([]));
    }, [archScope, lockScope]);

    // "绑定设备"多选的候选 = 该模板 (框架) 下的设备清单; 复用模板关联清单接口, 按模板缓存.
    const [scopeItems, setScopeItems] = useState<Record<string, BoundItem[]>>(
        {},
    );
    const editorScope = archScope ? editor.archCode : '';
    useEffect(() => {
        if (!editorScope || scopeItems[editorScope]) return;
        client
            .scope(editorScope)
            .then((r) =>
                setScopeItems((prev) => ({ ...prev, [editorScope]: r.items })),
            )
            .catch(() =>
                setScopeItems((prev) => ({ ...prev, [editorScope]: [] })),
            );
    }, [client, editorScope, scopeItems]);

    // 类别树: 归属模板下拉 (材料组)
    const categoryTree = useCategoryTree();

    const { byKey: fieldDictByKey, groups: fieldDictGroups } =
        useSchemaFieldDict();

    // 组内 页签 → 分组 (仅设备组, 2026-10-23; 与参数模板编辑器同一套, 见 components/fieldLayout)
    const ed = useLayoutEditor<SchemaGroupField>({
        fields: editor.fields,
        setFields: (next) => setEditor({ ...editor, fields: next }),
        makeField: (tab, sec) =>
            tab ? { key: '', label: '', tab, sec } : { key: '', label: '' },
        setError: (msg) => {
            if (msg) alertDialog(msg);
        },
    });
    // 打开 (换了一个组) 时载入它的布局
    useEffect(() => {
        ed.setLayout(editor.fieldLayout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor.groupKey, editor.isNew]);
    const layoutOn = !!archScope && ed.grouped;

    const patchField = (i: number, p: Partial<SchemaGroupField>) => {
        const next = [...editor.fields];
        next[i] = { ...next[i], ...p };
        setEditor({ ...editor, fields: next });
    };

    const dictToGroupField = (d: SchemaField): Partial<SchemaGroupField> => {
        // 枚举 (options/optionsSource) 不再由模板携带: 一律继承 SSOT, 读路径由后端富化注入.
        const hasEnum = !!d.enumsSource || parseFieldEnums(d).length > 0;
        return {
            key: d.fieldKey,
            label: suggestFieldLabel(d),
            unit: d.unit || undefined,
            // 组字段值类型缺省数值; 字典 array → 列表字段 (逗号分隔录入, 存字符串数组);
            // 其余非数值且无枚举的字段按自由文本录入
            type:
                d.fieldType === 'array'
                    ? 'array'
                    : !hasEnum && d.fieldType && d.fieldType !== 'number'
                      ? 'str'
                      : undefined,
        };
    };

    // 删字段要二次确认: × 按钮小且紧贴字段行, 误触会静默丢字段 (保存后才发现)
    const removeEditorField = async (i: number) => {
        const f = editor.fields[i];
        const name = f?.label || f?.key || `第 ${i + 1} 行`;
        if (
            !(await confirmDialog(
                `确认删除字段「${name}」? 保存后该字段从本字段组移除.`,
            ))
        )
            return;
        setEditor({
            ...editor,
            fields: editor.fields.filter((_, idx) => idx !== i),
        });
    };

    const save = async () => {
        if (!editor.groupKey || !editor.label) {
            alertDialog('组编码 / 显示名 不能为空');
            return;
        }
        // Schema 显式绑定: 组必须归属某个模板 (设备=工艺框架, 材料=一级类别), 不再有"通用/全部"组
        if (archScope && !editor.archCode) {
            alertDialog(
                '必须选择工艺框架: 字段组归属于框架模板, 不再支持通用组',
            );
            return;
        }
        if (categoryScope && !editor.cat1) {
            alertDialog(
                '必须选择一级类别: 字段组归属于类别模板, 不再支持"全部材料"组',
            );
            return;
        }
        // 只清洗 key/label/unit + 元属性 (type). 枚举 (options/optionsSource) 不再由模板存储:
        // 定律"模板一切来源于 schema_field" —— 枚举来源以 SSOT 为准, 读路径由后端按 schema_field 富化注入.
        const cleanFields = editor.fields
            .filter((f) => f.key.trim() && f.label.trim())
            .map((f) => {
                const out: SchemaGroupField = { key: f.key, label: f.label };
                if (f.unit) out.unit = f.unit;
                if (f.type) out.type = f.type;
                if (layoutOn) {
                    out.tab = f.tab;
                    out.sec = f.sec;
                }
                if (fieldRequired && f.required) out.required = true;
                return out;
            });
        if (cleanFields.length === 0) {
            alertDialog('至少配置一个字段 (key + 显示名)');
            return;
        }
        if (layoutOn) {
            const outside = cleanFields.filter(
                (f) => !fitsLayout(f, ed.layout),
            );
            if (outside.length > 0) {
                alertDialog(
                    `${outside.length} 个字段不在任何分组里 (如 ${outside[0].key}), 请删除或在分组里重新添加`,
                );
                return;
            }
        }
        // 设备组的布局 (材料理化组后端没有这项, 不发). 后端口径: 不传 = 不改, 空串 = 取消分组;
        // 原本就没分组的组不发 (存量组保存不碰该列), 原本有分组、现在关掉了发空串
        const deviceExtra = archScope
            ? {
                  fieldLayout: layoutOn
                      ? (stringifyLayout(ed.layout) ?? '')
                      : editor.fieldLayout
                        ? ''
                        : undefined,
              }
            : {};
        if (
            matrixLayout &&
            editor.layout === 'matrix' &&
            !editor.rowSource.trim()
        ) {
            alertDialog('矩阵布局需要选择行来源枚举');
            return;
        }
        if (onDraft) {
            onDraft({
                groupKey: editor.groupKey,
                label: editor.label,
                src: editor.src,
                fields: JSON.stringify(cleanFields),
                isEnabled: editor.isEnabled,
                ...(archScope && editor.deviceKeys.length
                    ? { deviceKeys: editor.deviceKeys }
                    : {}),
                ...(categoryScope && editor.cat2Keys.length
                    ? { cat2Keys: editor.cat2Keys }
                    : {}),
                ...(matrixLayout
                    ? {
                          layout: editor.layout,
                          rowSource: editor.rowSource.trim(),
                          rowLabel: editor.rowLabel.trim(),
                      }
                    : {}),
                ...deviceExtra,
            });
            return;
        }
        try {
            const fieldsJSON = JSON.stringify(cleanFields);
            const scope = {
                ...(categoryScope
                    ? { cat1: editor.cat1, cat2Keys: editor.cat2Keys }
                    : {}),
                ...(archScope
                    ? {
                          archCode: editor.archCode,
                          deviceKeys: editor.deviceKeys,
                      }
                    : {}),
                ...(matrixLayout
                    ? {
                          layout: editor.layout,
                          rowSource: editor.rowSource.trim(),
                          rowLabel: editor.rowLabel.trim(),
                      }
                    : {}),
                ...deviceExtra,
            };
            const res = editor.isNew
                ? await client.create({
                      groupKey: editor.groupKey,
                      label: editor.label,
                      src: editor.src,
                      ...scope,
                      fields: fieldsJSON,
                      isEnabled: editor.isEnabled,
                  })
                : await client.update(
                      editor.groupKey,
                      {
                          label: editor.label,
                          src: editor.src,
                          ...scope,
                          fields: fieldsJSON,
                          isEnabled: editor.isEnabled,
                      },
                      editor.origScopeCode,
                  );
            onSaved(editor.groupKey);
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`保存失败: ${errMsg(e)}`);
        }
    };

    return (
        <Modal
            open
            title={
                editor.isNew
                    ? `新建${domainLabel}字段组`
                    : `编辑字段组 · ${editor.groupKey}`
            }
            onClose={onClose}
            width={640}
        >
            <div className="space-y-3">
                {lockScope && scopeHint && (
                    <div className="infobox text-xs">
                        归属模板: <b>{scopeHint}</b> — 由当前新建流程决定,
                        不在这里改; 其余选项与「
                        {archScope ? '设备模板' : '材料模板'}
                        」页的新建完全一致。
                        {archScope
                            ? ' 绑定设备不勾 = 该框架全部设备; 勾了的话, 正在新建的这台设备会自动一并绑上。'
                            : ' 绑定二级类别不勾 = 该类别全部材料; 勾了但不含当前材料的二级类别时, 本组不会出现在这一步。'}
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">
                            组编码 (业务键, 建后不可改)
                        </label>
                        <input
                            className="inp w-full font-mono"
                            value={editor.groupKey}
                            disabled={!editor.isNew}
                            onChange={(e) =>
                                setEditor({
                                    ...editor,
                                    groupKey: e.target.value,
                                })
                            }
                            placeholder="thermal / maintenance …"
                        />
                    </div>
                    <div>
                        <label className="field-label">显示名</label>
                        <input
                            className="inp w-full"
                            value={editor.label}
                            onChange={(e) =>
                                setEditor({
                                    ...editor,
                                    label: e.target.value,
                                })
                            }
                            placeholder="热参数 / 维护信息 …"
                        />
                    </div>
                </div>
                {archScope && !lockScope && (
                    <div>
                        <label className="field-label">
                            归属模板 · 工艺框架 (必选: 一大类一模板, 组必须归属)
                        </label>
                        <SearchSelect
                            className="w-full"
                            value={editor.archCode}
                            onChange={(v) =>
                                setEditor({
                                    ...editor,
                                    archCode: v,
                                })
                            }
                            placeholder="选择框架模板…"
                            options={archs.map((a) => ({
                                value: a.code,
                                label: `${a.icon} ${a.label}`,
                            }))}
                        />
                    </div>
                )}
                {archScope && (
                    <DeviceKeysPicker
                        title="绑定设备 (不勾 = 该框架全部设备; 勾选后只有这些设备显示本组)"
                        items={scopeItems[editor.archCode] || []}
                        value={editor.deviceKeys}
                        onChange={(deviceKeys) =>
                            setEditor({ ...editor, deviceKeys })
                        }
                        emptyHint={
                            editor.archCode
                                ? '该框架暂无设备'
                                : '先选工艺框架, 再选框架内的设备'
                        }
                        allHint="当前对该框架全部设备生效"
                    />
                )}
                {categoryScope && !lockScope && (
                    <div>
                        <label className="field-label">
                            归属模板 · 一级类别 (必选: 一大类一模板)
                        </label>
                        <SearchSelect
                            className="w-full"
                            value={editor.cat1}
                            onChange={(v) =>
                                // 换类别时清空二级绑定 (旧清单不属于新类别, 留着必被后端拒绝)
                                setEditor({
                                    ...editor,
                                    cat1: v,
                                    cat2Keys: [],
                                })
                            }
                            placeholder="选择类别模板…"
                            options={categoryTree.map((t) => ({
                                value: t.categoryCode,
                                label: `${t.nameZh} / ${t.nameEn}`,
                            }))}
                        />
                    </div>
                )}
                {categoryScope && (
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="field-label mb-0">
                                绑定二级类别 (不勾 = 该类别全部材料;
                                勾选后只有这些二级类别的材料显示本组)
                            </label>
                            <div className="flex gap-2 text-xs">
                                <button
                                    type="button"
                                    className="text-brand-600 hover:underline"
                                    onClick={() =>
                                        setEditor({
                                            ...editor,
                                            cat2Keys: (
                                                categoryTree.find(
                                                    (t) =>
                                                        t.categoryCode ===
                                                        editor.cat1,
                                                )?.children || []
                                            ).map((c) => c.categoryCode),
                                        })
                                    }
                                >
                                    全选
                                </button>
                                <button
                                    type="button"
                                    className="text-slate-400 hover:text-slate-600"
                                    onClick={() =>
                                        setEditor({
                                            ...editor,
                                            cat2Keys: [],
                                        })
                                    }
                                >
                                    清空 (=全部材料)
                                </button>
                            </div>
                        </div>
                        {!editor.cat1 ? (
                            <div className="text-xs text-slate-300 py-2">
                                先选一级类别, 再选类别内的二级类别
                            </div>
                        ) : (
                              categoryTree.find((t) => t.categoryCode === editor.cat1)
                                  ?.children || []
                          ).length === 0 ? (
                            <div className="text-xs text-slate-300 py-2">
                                该类别暂无二级类别
                            </div>
                        ) : (
                            <div className="max-h-40 overflow-auto border border-slate-100 rounded-md p-2 grid grid-cols-2 gap-1">
                                {(
                                    categoryTree.find(
                                        (t) => t.categoryCode === editor.cat1,
                                    )?.children || []
                                ).map((c) => {
                                    const checked = editor.cat2Keys.includes(
                                        c.categoryCode,
                                    );
                                    return (
                                        <label
                                            key={c.id}
                                            className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer"
                                            title={c.nameEn}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() =>
                                                    setEditor({
                                                        ...editor,
                                                        cat2Keys: checked
                                                            ? editor.cat2Keys.filter(
                                                                  (k) =>
                                                                      k !==
                                                                      c.categoryCode,
                                                              )
                                                            : [
                                                                  ...editor.cat2Keys,
                                                                  c.categoryCode,
                                                              ],
                                                    })
                                                }
                                            />
                                            <span className="truncate">
                                                {c.nameZh || c.nameEn}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                        <div className="text-[11px] text-slate-400 mt-1">
                            已绑定 {editor.cat2Keys.length} 个二级类别
                            {editor.cat2Keys.length === 0 &&
                                ' — 当前对该类别全部材料生效'}
                        </div>
                    </div>
                )}
                {matrixLayout && (
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="field-label">布局</label>
                            <SearchSelect
                                className="w-full"
                                value={editor.layout}
                                onChange={(v) =>
                                    setEditor({
                                        ...editor,
                                        layout: v,
                                    })
                                }
                                options={LAYOUT_OPTS}
                            />
                        </div>
                        {editor.layout === 'matrix' && (
                            <div>
                                <label className="field-label">
                                    行来源枚举 (行数据来自该枚举,
                                    条目在字典维护里增删)
                                </label>
                                <SearchSelect
                                    className="w-full"
                                    value={editor.rowSource}
                                    onChange={(v) =>
                                        setEditor({
                                            ...editor,
                                            rowSource: v,
                                            // 切枚举时预填标题为枚举名, 已手改过则保留
                                            rowLabel:
                                                editor.rowLabel.trim() === '' ||
                                                editor.rowLabel ===
                                                    enumLabel(editor.rowSource)
                                                    ? enumLabel(v)
                                                    : editor.rowLabel,
                                        })
                                    }
                                    placeholder="选择枚举…"
                                    options={[
                                        {
                                            value: 'mm_group',
                                            label: '加工模式 (mm_group)',
                                        },
                                        ...enumCats
                                            .filter((c) => !c.scoped)
                                            .map((c) => ({
                                                value: c.code,
                                                label: `${c.label} (${c.code})`,
                                            })),
                                    ]}
                                />
                            </div>
                        )}
                    </div>
                )}
                {matrixLayout && editor.layout === 'matrix' && (
                    <div>
                        <label className="field-label">
                            行维度列名 (矩阵表头第一列, 可改; 如 波长 (nm))
                        </label>
                        <input
                            className="inp w-full"
                            value={editor.rowLabel}
                            placeholder="波长 (nm)"
                            onChange={(e) =>
                                setEditor({
                                    ...editor,
                                    rowLabel: e.target.value,
                                })
                            }
                        />
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">来源说明 (可选)</label>
                        <input
                            className="inp w-full"
                            value={editor.src}
                            onChange={(e) =>
                                setEditor({
                                    ...editor,
                                    src: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div>
                        <label className="field-label">启用</label>
                        <SearchSelect
                            className="w-full"
                            value={editor.isEnabled ? '1' : '0'}
                            onChange={(v) =>
                                setEditor({
                                    ...editor,
                                    isEnabled: v === '1',
                                })
                            }
                            options={ENABLED_OPTS}
                        />
                    </div>
                </div>
                <div>
                    <div className="flex items-center justify-between mb-1 gap-3">
                        <label className="field-label">
                            字段 (key + 显示名 + 单位 + 枚举选项)
                        </label>
                        {archScope && <LayoutTabActions ed={ed} canUpdate />}
                        {!layoutOn && (
                            <button
                                type="button"
                                className="text-xs text-brand-600 hover:underline"
                                onClick={() => ed.addField()}
                            >
                                + 加字段
                            </button>
                        )}
                    </div>
                    <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                        {(() => {
                            const renderRow = ({
                                f,
                                idx: i,
                            }: {
                                f: SchemaGroupField;
                                idx: number;
                            }) => (
                                <div className="flex gap-2 items-center">
                                    <SchemaFieldKeySelect
                                        value={f.key}
                                        byKey={fieldDictByKey}
                                        groups={fieldDictGroups}
                                        className="flex-1 min-w-0"
                                        onSelect={(d, key) =>
                                            patchField(
                                                i,
                                                d
                                                    ? dictToGroupField(d)
                                                    : { key },
                                            )
                                        }
                                    />
                                    <input
                                        className="inp flex-1 text-xs"
                                        placeholder="显示名"
                                        value={f.label}
                                        onChange={(e) =>
                                            patchField(i, {
                                                label: e.target.value,
                                            })
                                        }
                                    />
                                    <input
                                        className="inp w-20 text-xs"
                                        placeholder="单位"
                                        value={f.unit || ''}
                                        onChange={(e) =>
                                            patchField(i, {
                                                unit: e.target.value,
                                            })
                                        }
                                    />
                                    {(() => {
                                        // 枚举只读, 一律继承 schema_field (SSOT): 模板不能在此发明/编辑枚举.
                                        const sf = fieldDictByKey.get(f.key);
                                        const src = sf?.enumsSource;
                                        const inline = sf
                                            ? parseFieldEnums(sf).map(String)
                                            : [];
                                        const hasEnum =
                                            !!src || inline.length > 0;
                                        const text = src
                                            ? `枚举←${src}`
                                            : inline.length
                                              ? `枚举: ${inline.join('/')}`
                                              : '无枚举';
                                        const title = src
                                            ? `值来自字典「${src}」(字段字典定义, 模板继承; 改动去 Schema 字段管理)`
                                            : inline.length
                                              ? `枚举来自字段字典: ${inline.join(', ')} (模板继承)`
                                              : '该字段在字段字典中未配枚举, 录入为自由输入';
                                        return (
                                            <span
                                                className={`w-36 text-xs truncate ${hasEnum ? 'text-emerald-600' : 'text-slate-300'}`}
                                                title={title}
                                            >
                                                {text}
                                            </span>
                                        );
                                    })()}
                                    {fieldRequired && (
                                        <label
                                            className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap"
                                            title="选了该类型的 SKU 必须填这个字段"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={!!f.required}
                                                onChange={(e) =>
                                                    patchField(i, {
                                                        required:
                                                            e.target.checked,
                                                    })
                                                }
                                            />
                                            必填
                                        </label>
                                    )}
                                    <button
                                        type="button"
                                        className="text-rose-400 hover:text-rose-600 text-sm px-1"
                                        title="删除该字段"
                                        onClick={() => removeEditorField(i)}
                                    >
                                        ×
                                    </button>
                                </div>
                            );
                            return archScope ? (
                                <LayoutFieldList
                                    ed={ed}
                                    canUpdate
                                    renderItem={renderRow}
                                    hint="建页签后, 本字段组在设备详情里按 页签 → 分组 嵌套展示"
                                />
                            ) : (
                                editor.fields.map((f, i) => (
                                    <React.Fragment key={i}>
                                        {renderRow({ f, idx: i })}
                                    </React.Fragment>
                                ))
                            );
                        })()}
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onClose}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={save}
                    >
                        保存
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default FieldGroupEditorModal;
